<script lang="ts">
const LUMINA_DEFAULT_PROVIDER_ID = 'lumina-proxy'
</script>
<script setup lang="ts">
import type {
  OnboardingStep,
  OnboardingStepGuard,
  OnboardingStepNextHandler,
  OnboardingStepPrevHandler,
} from './types'

import { isCustomProvidersDisabled } from '@proj-airi/stage-shared'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref } from 'vue'

import StepWelcome from './step-welcome.vue'

import { useAnalytics } from '../../../../composables/use-analytics'
import { useConsciousnessStore } from '../../../../stores/modules/consciousness'
import { useProvidersStore } from '../../../../stores/providers'

interface Emits {
  (e: 'configured'): void
  (e: 'skipped'): void
}

const props = withDefaults(defineProps<{
  extraSteps?: OnboardingStep[]
}>(), {
  extraSteps: () => [],
})
const emit = defineEmits<Emits>()
const step = ref(0)
const direction = ref<'next' | 'previous'>('next')
const { trackOnboardingCompleted, trackOnboardingStarted, trackOnboardingStepCompleted } = useAnalytics()

const providersStore = useProvidersStore()
const consciousnessStore = useConsciousnessStore()
const {
  activeProvider,
  activeModel,
} = storeToRefs(consciousnessStore)

const requestPreviousStep: OnboardingStepPrevHandler = () => {
  return navigatePrevious()
}

// NOTICE:
// The `configData` argument is accepted and ignored. It carried the pasted API
// key from the deleted provider-configuration step; `saveProviderConfiguration`,
// which wrote that key into the browser's provider store, is deleted with it.
// The signature is kept because `OnboardingStepNextHandler` is the shared contract
// every step is typed against, including `extraSteps` supplied by the host app.
const requestNextStep: OnboardingStepNextHandler = async () => {
  await navigateNext()
}

/**
 * The Lumina platform default. Chat goes through `lumina-airi-proxy`, which injects the
 * provider key server-side, so a signed-in user has a working assistant with nothing to
 * configure and no key to paste.
 */


/**
 * Completes onboarding straight from the welcome step.
 *
 * Selecting a provider and pasting a key is upstream's flow; on Lumina the credentials
 * already exist server-side, so the only honest first screen is a single Enter. The model
 * list is fetched here rather than assumed, because the proxy decides what it serves and
 * enumerating it needs the user's live JWT.
 */
async function enterWithPlatformDefaults() {
  if (!activeProvider.value)
    activeProvider.value = LUMINA_DEFAULT_PROVIDER_ID

  await nextTick()

  try {
    await consciousnessStore.loadModelsForProvider(activeProvider.value)

    // Leave an existing choice alone; only fill an empty selection, so a returning user
    // who already picked a model does not get silently reassigned.
    if (!activeModel.value) {
      const [firstModel] = providersStore.getModelsForProvider(activeProvider.value)
      if (firstModel)
        activeModel.value = firstModel.id
    }
  }
  catch (err) {
    // A failed model fetch must not trap the user on the welcome screen. They land in the
    // app and the ordinary provider error surfaces there, where it is actionable.
    console.error('[onboarding] Failed to prepare the Lumina provider:', err)
  }

  await handleSave()
}

/**
 * Onboarding is one screen.
 *
 * Upstream's flow was welcome -> pick a provider -> paste an API key -> pick a
 * model. Every one of those steps asks for something the platform already knows:
 * the proxy holds the provider credentials server-side and resolves the signed-in
 * user's own key from their vault. So the provider-selection, provider-configuration
 * and model-selection steps are REMOVED, not hidden — a hidden key field is still a
 * key field someone can be routed back to.
 *
 * `extraSteps` is kept: the analytics notice is consent, not configuration, and it
 * is the one thing here that genuinely has to be asked.
 */
const allSteps = computed<OnboardingStep[]>(() => {
  const coreSteps: OnboardingStep[] = [
    {
      id: 'welcome',
      component: StepWelcome,
      props: () => ({
        customProviderSetupEnabled: !isCustomProvidersDisabled(),
        onEnter: enterWithPlatformDefaults,
      }),
    },
    ...props.extraSteps.map(step => ({
      ...step,
      props: () => ({
        ...step.props?.(),
      }),
    })),
  ]

  return coreSteps
})

const currentStep = computed(() => allSteps.value[step.value] ?? null)
const isLastStep = computed(() => step.value === allSteps.value.length - 1)
const currentStepProps = computed(() => currentStep.value?.props?.() ?? {})

async function handleSave() {
  trackOnboardingStepCompleted(currentStep.value?.id ?? 'unknown')
  // Reported as the platform default rather than a user choice: there is no
  // provider picker any more, so anything else would be inventing a selection
  // the user never made.
  trackOnboardingCompleted({
    selected_provider_type: 'custom',
    selected_provider_id: LUMINA_DEFAULT_PROVIDER_ID,
    selected_use_case: 'unknown',
  })
  emit('configured')
}

async function canPassGuard(guard?: OnboardingStepGuard) {
  if (!guard)
    return true

  return await guard()
}

async function navigateNext() {
  if (!currentStep.value)
    return

  if (!(await canPassGuard(currentStep.value.beforeNext)))
    return

  if (isLastStep.value) {
    await handleSave()
    return
  }

  trackOnboardingStepCompleted(currentStep.value.id)
  direction.value = 'next'
  step.value++
}

async function navigatePrevious() {
  if (!currentStep.value || step.value <= 0)
    return

  if (!(await canPassGuard(currentStep.value.beforePrev)))
    return

  direction.value = 'previous'
  step.value--
}

onMounted(() => {
  trackOnboardingStarted({ entry: 'app_start' })
})
</script>

<template>
  <div class="onboarding-step-container" min-h-0 flex flex-1 flex-col>
    <Transition :name="direction === 'next' ? 'slide-next' : 'slide-prev'" mode="out-in">
      <component
        :is="currentStep.component"
        v-if="currentStep"
        :key="currentStep.id"
        class="flex flex-1 flex-col"
        v-bind="currentStepProps"
        :on-next="requestNextStep"
        :on-previous="requestPreviousStep"
      />
    </Transition>
  </div>
</template>

<style scoped>
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  will-change: transform, opacity;
}

.slide-next-enter-active {
  animation: onboarding-slide-next-in 0.2s ease-in-out both;
}

.slide-next-leave-active {
  animation: onboarding-slide-next-out 0.2s ease-in-out both;
}

.slide-prev-enter-active {
  animation: onboarding-slide-prev-in 0.2s ease-in-out both;
}

.slide-prev-leave-active {
  animation: onboarding-slide-prev-out 0.2s ease-in-out both;
}

@keyframes onboarding-slide-next-in {
  from {
    transform: translateX(2rem);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes onboarding-slide-next-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(-2rem);
    opacity: 0;
  }
}

@keyframes onboarding-slide-prev-in {
  from {
    transform: translateX(-2rem);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes onboarding-slide-prev-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(2rem);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .slide-next-enter-active,
  .slide-next-leave-active,
  .slide-prev-enter-active,
  .slide-prev-leave-active {
    animation-duration: 1ms;
  }
}
</style>
